import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const SLIDES = [
    {
        img: "https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format",
        alt: "Cucina moderna con isola ed elettrodomestici in acciaio",
        caption: "La cucina, il cuore di casa",
    },
    {
        img: "https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format",
        alt: "Frigorifero in acciaio in una cucina moderna",
        caption: "Frigoriferi capienti, di ogni marca",
    },
    {
        img: "https://images.unsplash.com/photo-1723902500132-7de6112d5627?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format",
        alt: "Forno e piano cottura in acciaio inossidabile",
        caption: "Forni e piani cottura di ultima generazione",
    },
    {
        img: "https://images.unsplash.com/photo-1783377996769-8ae94b0bbac9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format",
        alt: "Forno doppio di design con maniglie in ottone",
        caption: "Elettrodomestici di design, li tocchi in negozio",
    },
];

export default function Catalogo() {
    const [slide, setSlide] = useState(0);
    const [pausa, setPausa] = useState(false);

    useEffect(() => {
        if (pausa) return;
        const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 4500);
        return () => clearInterval(t);
    }, [pausa]);

    const vai = (d) => setSlide((s) => (s + d + SLIDES.length) % SLIDES.length);

    return (
        <section id="catalogo" className="py-24 md:py-32 border-t border-line scroll-mt-16">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <SectionHeading
                        eyebrow="Offerte del mese"
                        title={
                            <>
                                Super offerte,
                                <br />
                                <span className="text-outline">pagate in negozio.</span>
                            </>
                        }
                    />
                    <Reveal delay={0.15}>
                        <p className="max-w-sm text-muted-foreground leading-relaxed">
                            Ogni mese le migliori offerte su elettrodomestici,
                            ricambi e ricambi Folletto: le trovi qui e in
                            negozio. Ordina su WhatsApp e paga direttamente da noi.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.1} className="mt-12">
                    <div
                        data-testid="offerte-slideshow"
                        className="relative overflow-hidden border border-line bg-card"
                        onMouseEnter={() => setPausa(true)}
                        onMouseLeave={() => setPausa(false)}
                    >
                        <div className="relative aspect-[16/10] md:aspect-[21/9]">
                            {SLIDES.map((s, i) => (
                                <motion.img
                                    key={i}
                                    src={s.img}
                                    alt={s.alt}
                                    data-testid={`slide-img-${i}`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: i === slide ? 1 : 0 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            ))}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />

                            <div className="absolute bottom-0 inset-x-0 p-5 md:p-7 flex items-end justify-between gap-4">
                                <AnimatePresence mode="wait">
                                    <motion.p
                                        key={slide}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.4 }}
                                        data-testid={`slide-caption-${slide}`}
                                        className="font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/90"
                                    >
                                        {SLIDES[slide].caption}
                                    </motion.p>
                                </AnimatePresence>
                                <div className="flex items-center gap-2">
                                    <button
                                        data-testid="slide-prev"
                                        onClick={() => vai(-1)}
                                        aria-label="Slide precedente"
                                        className="h-9 w-9 flex items-center justify-center bg-black/50 backdrop-blur text-white border border-white/25 hover:bg-primary hover:border-primary transition-colors"
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </button>
                                    <button
                                        data-testid="slide-next"
                                        onClick={() => vai(1)}
                                        aria-label="Slide successiva"
                                        className="h-9 w-9 flex items-center justify-center bg-black/50 backdrop-blur text-white border border-white/25 hover:bg-primary hover:border-primary transition-colors"
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 py-4">
                            {SLIDES.map((_, i) => (
                                <button
                                    key={i}
                                    data-testid={`slide-dot-${i}`}
                                    onClick={() => setSlide(i)}
                                    aria-label={`Vai alla slide ${i + 1}`}
                                    className={`h-1.5 transition-all duration-300 ${i === slide ? "w-8 bg-primary" : "w-3 bg-muted-foreground/40 hover:bg-muted-foreground/70"}`}
                                />
                            ))}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}